import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Star as StarIcon,
  StarOutline as StarOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';

interface CustomerEmail {
  id: string;
  email: string;
  isPrimary: boolean;
}

interface CustomerEmailManagerProps {
  customerId: string;
}

export const CustomerEmailManager: React.FC<CustomerEmailManagerProps> = ({ customerId }) => {
  const notify = useNotify();
  const [emails, setEmails] = useState<CustomerEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<CustomerEmail | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    isPrimary: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
  const accessToken = localStorage.getItem('accessToken');
  const tenantCode = localStorage.getItem('currentTenant')
    ? JSON.parse(localStorage.getItem('currentTenant')!).code
    : 'default';

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'x-tenant-code': tenantCode,
    }),
    [accessToken, tenantCode]
  );

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${baseUrl}/crm/customers/${customerId}/emails`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch emails');
      }

      const data = await response.json();
      setEmails(data);
    } catch (error) {
      notify('Failed to load customer emails', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [customerId, baseUrl, headers, notify]);

  useEffect(() => {
    if (customerId) {
      fetchEmails();
    }
  }, [customerId, fetchEmails]);

  const handleAdd = () => {
    setEditingEmail(null);
    setFormData({
      email: '',
      isPrimary: emails.length === 0, // First email is primary by default
    });
    setDialogOpen(true);
  };

  const handleEdit = (email: CustomerEmail) => {
    setEditingEmail(email);
    setFormData({
      email: email.email,
      isPrimary: email.isPrimary,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (emailId: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this email address?')) {
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/crm/customers/${customerId}/emails/${emailId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete email');
      }

      notify('Email deleted successfully', { type: 'success' });
      fetchEmails();
    } catch (error) {
      notify('Failed to delete email', { type: 'error' });
    }
  };

  const handleSetPrimary = async (emailId: string) => {
    try {
      // First, unset all primary emails
      const updatePromises = emails.map(email =>
        fetch(`${baseUrl}/crm/customers/${customerId}/emails/${email.id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ isPrimary: email.id === emailId }),
        })
      );

      await Promise.all(updatePromises);
      notify('Primary email updated successfully', { type: 'success' });
      fetchEmails();
    } catch (error) {
      notify('Failed to update primary email', { type: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!formData.email) {
      notify('Email address is required', { type: 'warning' });
      return;
    }

    try {
      setIsSubmitting(true);

      const emailData = {
        email: formData.email,
        isPrimary: formData.isPrimary,
        customerId,
      };

      let response;
      if (editingEmail) {
        response = await fetch(`${baseUrl}/crm/customers/${customerId}/emails/${editingEmail.id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(emailData),
        });
      } else {
        response = await fetch(`${baseUrl}/crm/customers/${customerId}/emails`, {
          method: 'POST',
          headers,
          body: JSON.stringify(emailData),
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save email');
      }

      notify(`Email ${editingEmail ? 'updated' : 'added'} successfully`, { type: 'success' });
      setDialogOpen(false);
      fetchEmails();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save email';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Typography>Loading emails...</Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Email Addresses
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd} size="small">
            Add Email
          </Button>
        </Stack>

        {emails.length === 0 ? (
          <Alert severity="info">
            No email addresses added yet. Click &quot;Add Email&quot; to add the first email
            address.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email Address</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Primary</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.map(email => (
                  <TableRow key={email.id}>
                    <TableCell>
                      <Typography variant="body2">{email.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleSetPrimary(email.id)}
                        disabled={email.isPrimary}
                        title={email.isPrimary ? 'Primary email' : 'Set as primary'}
                      >
                        {email.isPrimary ? (
                          <StarIcon color="warning" />
                        ) : (
                          <StarOutlineIcon color="action" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(email)}
                          title="Edit email"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(email.id)}
                          color="error"
                          title="Delete email"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Email Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {editingEmail ? 'Edit Email Address' : 'Add Email Address'}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setDialogOpen(false)} 
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Email Address *"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                required
                error={!formData.email}
                helperText={!formData.email ? 'Email address is required' : ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />

              <Box sx={{ 
                p: 2, 
                borderRadius: '8px', 
                border: '1px solid #e0e0e0',
                backgroundColor: '#fafafa'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPrimary}
                      onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Set as primary email address
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.isPrimary
                          ? 'This will be used as the default email for communications'
                          : 'Enable to make this the primary contact email'
                        }
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {formData.isPrimary && emails.some(e => e.isPrimary && e.id !== editingEmail?.id) && (
                <Alert 
                  severity="warning"
                  sx={{
                    borderRadius: '8px',
                    '& .MuiAlert-message': {
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  Setting this as primary will remove the primary status from the current primary
                  email address.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.email || isSubmitting}
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : editingEmail ? (
                'Update Email'
              ) : (
                'Add Email'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
